import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Keyboard,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { useSetPasswordMutation } from '@/store/api';
import { useToast } from '@/contexts/admin/ToastContext';
import { useAccountColors } from '@/constants/residents/mobile/accountColors';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { useAccountSectionStyles } from '@/constants/residents/mobile/accountSectionStyles';
import { SVG_COMPONENTS } from '@/assets/admin';
import { strings } from '@/constants/residents/strings';
import { AuthStackParamList } from '@/navigation/residents/mobile/AuthNavigator';

const CloseIcon = SVG_COMPONENTS.CANCEL_X;
const EyeOpenIcon = SVG_COMPONENTS.EYE_OPEN;
const EyeClosedIcon = SVG_COMPONENTS.EYE_CLOSED;
const WarningIcon = SVG_COMPONENTS.WARNING;

type SetPasswordRouteProp = RouteProp<AuthStackParamList, 'SetPassword'>;

const MIN_PASSWORD_LENGTH = 12;

export const SetPasswordScreen: React.FC = () => {
  const ACCOUNT_COLORS = useAccountColors();
  const accountSectionStyles = useAccountSectionStyles();
  const theme = useThemeColors();
  const { semantic } = theme;
  const styles = useMemo(() => makeStyles(ACCOUNT_COLORS, theme), [ACCOUNT_COLORS, theme]);
  const navigation = useNavigation<any>();
  const route = useRoute<SetPasswordRouteProp>();
  const insets = useSafeAreaInsets();

  const token = route.params?.token || '';

  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const [newPasswordError, setNewPasswordError] = useState('');
  const [repeatPasswordError, setRepeatPasswordError] = useState('');

  const { showSuccess, showError } = useToast();

  const repeatPasswordRef = useRef<TextInput | null>(null);

  const [setPassword, { isLoading }] = useSetPasswordMutation();

  const handleClose = useCallback(() => {
    Keyboard.dismiss();
    navigation.navigate('Login');
  }, [navigation]);

  const validateForm = useCallback((): boolean => {
    let isValid = true;

    setNewPasswordError('');
    setRepeatPasswordError('');

    if (!newPassword) {
      setNewPasswordError('New password is required');
      isValid = false;
    } else if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setNewPasswordError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      isValid = false;
    }

    if (!repeatPassword) {
      setRepeatPasswordError('Please confirm your new password');
      isValid = false;
    } else if (newPassword !== repeatPassword) {
      setRepeatPasswordError('Passwords do not match');
      isValid = false;
    }

    return isValid;
  }, [newPassword, repeatPassword]);

  const handleConfirm = useCallback(async () => {
    Keyboard.dismiss();

    if (!token) {
      showError('Invalid or missing token. Please contact your property manager.');
      setTimeout(() => {
        navigation.navigate('Login');
      }, 2000);
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      const response = await setPassword({
        token,
        password: newPassword,
      }).unwrap();

      if ((response as any)?.success) {
        showSuccess('Password set successfully! Redirecting to login...');
        setTimeout(() => {
          navigation.navigate('Login');
        }, 2000);
      } else {
        showError('Failed to set password');
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to set password';
      showError(errorMessage);
    }
  }, [token, validateForm, newPassword, setPassword, showSuccess, showError, navigation]);

  const renderPasswordInput = (
    label: string,
    value: string,
    onChangeText: (text: string) => void,
    showPassword: boolean,
    onToggleVisibility: () => void,
    error: string,
    placeholder: string,
    ref?: React.RefObject<TextInput | null>,
    onSubmitEditing?: () => void,
    returnKeyType?: 'next' | 'done',
    hint?: string,
  ) => (
    <View style={styles.inputGroup}>
      <Text style={styles.inputLabel}>{label}</Text>
      <View style={[styles.inputContainer, error ? styles.inputContainerError : null]}>
        <TextInput
          ref={ref}
          style={styles.input}
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={ACCOUNT_COLORS.textMuted}
          secureTextEntry={!showPassword}
          autoCapitalize="none"
          autoCorrect={false}
          returnKeyType={returnKeyType || 'next'}
          onSubmitEditing={onSubmitEditing}
          editable={!isLoading}
        />
        <TouchableOpacity
          style={styles.eyeButton}
          onPress={onToggleVisibility}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          {showPassword ? (
            EyeOpenIcon ? <EyeOpenIcon width={20} height={20} color={ACCOUNT_COLORS.textMuted} /> : null
          ) : (
            EyeClosedIcon ? <EyeClosedIcon width={20} height={20} color={ACCOUNT_COLORS.textMuted} /> : null
          )}
        </TouchableOpacity>
      </View>
      {hint && !error && (
        <View style={styles.hintContainer}>
          {WarningIcon && <WarningIcon width={14} height={14} color={ACCOUNT_COLORS.textMuted} />}
          <Text style={styles.hintText}>{hint}</Text>
        </View>
      )}
      {error ? (
        <View style={styles.errorContainer}>
          {WarningIcon && <WarningIcon width={14} height={14} color={semantic.status.error} />}
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : null}
    </View>
  );

  return (
    <SafeAreaView style={accountSectionStyles.modalContainer} edges={['top']}>
      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
      >
        <View style={styles.header}>
          <Text style={accountSectionStyles.headerTitle}>Set password</Text>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            {CloseIcon && <CloseIcon width={24} height={24} color={ACCOUNT_COLORS.textPrimary} />}
          </TouchableOpacity>
        </View>

        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <Text style={styles.instructions}>
            Welcome to Resio! Create a password to activate your account. Your password should be at least {MIN_PASSWORD_LENGTH} characters long and include a mix of uppercase and lowercase letters, numbers, and special characters.
          </Text>

          {renderPasswordInput(
            'New password',
            newPassword,
            setNewPassword,
            showNewPassword,
            () => setShowNewPassword(!showNewPassword),
            newPasswordError,
            strings.login.field.passwordPlaceholder,
            undefined,
            () => repeatPasswordRef.current?.focus(),
            'next',
            `Your password must be at least ${MIN_PASSWORD_LENGTH} characters`,
          )}

          {renderPasswordInput(
            'Repeat new password',
            repeatPassword,
            setRepeatPassword,
            showRepeatPassword,
            () => setShowRepeatPassword(!showRepeatPassword),
            repeatPasswordError,
            strings.login.field.passwordPlaceholder,
            repeatPasswordRef,
            handleConfirm,
            'done',
          )}
        </ScrollView>

        <View style={[styles.footer, { paddingBottom: Math.max(insets.bottom, 24) }]}>
          <TouchableOpacity
            style={accountSectionStyles.cancelButtonTall}
            onPress={handleClose}
            disabled={isLoading}
          >
            <Text style={accountSectionStyles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[accountSectionStyles.confirmButtonTall, isLoading && accountSectionStyles.confirmButtonDisabled]}
            onPress={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <ActivityIndicator size="small" color={ACCOUNT_COLORS.textPrimary} />
            ) : (
              <Text style={accountSectionStyles.confirmButtonText}>Confirm</Text>
            )}
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

type AccountColors = ReturnType<typeof useAccountColors>;

const makeStyles = (ACCOUNT_COLORS: AccountColors, { semantic }: ThemeContextValue) => StyleSheet.create({
  keyboardAvoidingView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ACCOUNT_COLORS.innerBorder,
    backgroundColor: ACCOUNT_COLORS.contentBackground,
  },
  closeButton: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollView: {
    flex: 1,
    backgroundColor: ACCOUNT_COLORS.contentBackground,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 24,
  },
  instructions: {
    fontSize: 14,
    fontWeight: '400',
    color: ACCOUNT_COLORS.textSecondary,
    lineHeight: 21,
    marginBottom: 24,
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: ACCOUNT_COLORS.textSecondary,
    lineHeight: 21,
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ACCOUNT_COLORS.background,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ACCOUNT_COLORS.innerBorder,
    height: 48,
  },
  inputContainerError: {
    borderColor: semantic.status.error,
  },
  input: {
    flex: 1,
    height: 48,
    paddingHorizontal: 16,
    fontSize: 14,
    color: ACCOUNT_COLORS.textPrimary,
  },
  eyeButton: {
    paddingHorizontal: 16,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  hintContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    gap: 6,
  },
  hintText: {
    fontSize: 12,
    fontWeight: '400',
    color: ACCOUNT_COLORS.textMuted,
    lineHeight: 18,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    gap: 6,
  },
  errorText: {
    fontSize: 12,
    fontWeight: '400',
    color: semantic.status.error,
    lineHeight: 18,
  },
  footer: {
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 20,
    paddingTop: 16,
    backgroundColor: ACCOUNT_COLORS.contentBackground,
    borderTopWidth: 1,
    borderTopColor: ACCOUNT_COLORS.innerBorder,
  },
});

export default SetPasswordScreen;
